import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AbilityContext } from "context/Ability";

// Constants
import { REDIRECT_LD, REDIRECT_PACE } from "constants/constants";

// Services
import { updateUserOrg } from "services";

// Hooks
import { useAuth } from "utils/hooks";
import { useAbility } from "@casl/react";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Store
import { toggleLoader } from "store/slice/UI";
import { fetchSession } from "store/slice/auth";
import { fetchUserOrgsThunk, getUserOrgs, TUserOrganizationsSlice } from "store/slice/sysadmin/userOrgs/userOrgsSlice";

const useChooseYourHub = (): {
    userOrgs: TUserOrganizationsSlice;
    updateSelectedOrg: (orgId: number) => Promise<void>;
} => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    const { sessionData } = useAuth();
    const userOrgs = useAppSelector(getUserOrgs);

    const redirectToInitialPage = useCallback(() => {
        if (sessionData === null) return;

        if (sessionData.authn.user.default_landing_page) {
            sessionData.authn.user.default_landing_page === "ld" && navigate(REDIRECT_LD);
            sessionData.authn.user.default_landing_page === "pace" && navigate(REDIRECT_PACE);
            dispatch(toggleLoader(false));
        } else {
            if (ability.can("see", "PACE") || ability.can("see", "PACE_ASSESSMENT")) {
                navigate(REDIRECT_PACE);
                dispatch(toggleLoader(false));
                return;
            }

            if (
                ability.can("see", "LD_FREE") ||
                ability.can("see", "LD_BASIC") ||
                ability.can("see", "LD_LIMITED") ||
                ability.can("see", "LD_TALENT")
            ) {
                navigate(REDIRECT_LD);
                dispatch(toggleLoader(false));
                return;
            }
        }
    }, [ability, dispatch, navigate, sessionData]);

    const updateSelectedOrg = async (orgId: number) => {
        try {
            dispatch(toggleLoader(true));
            const { data } = await updateUserOrg(orgId);
            if (data.Info === "OK") {
                await dispatch(fetchSession());
                redirectToInitialPage();
            }
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(toggleLoader(false));
        }
    };

    useEffect(() => {
        const initialFetch = async () => {
            if (sessionData && sessionData.authn.user.org_selected.id) {
                redirectToInitialPage();
                return;
            }

            if (userOrgs.status === "pristine") {
                await dispatch(fetchUserOrgsThunk());
            }
        };

        initialFetch();
    }, [dispatch, redirectToInitialPage, sessionData, userOrgs]);

    return {
        userOrgs,
        updateSelectedOrg,
    };
};

export default useChooseYourHub;
